import { useEffect, useRef, useCallback } from 'react';

const SOURCES = [
  { label: 'Shoptet', color: '#16A34A', abbr: 'Sh' },
  { label: 'Stripe', color: '#635BFF', abbr: 'S' },
  { label: 'Zásilkovna', color: '#DC2626', abbr: 'Z' },
  { label: 'Mews', color: '#2563EB', abbr: 'M' },
  { label: 'PayPal', color: '#003087', abbr: 'P' },
  { label: 'WooCom', color: '#7C3AED', abbr: 'W' },
];

const TARGETS = [
  { label: 'Money S3', abbr: 'M3' },
  { label: 'Pohoda', abbr: 'Po' },
  { label: 'Abra', abbr: 'Ab' },
];

const LOG_MESSAGES = [
  { count: 45, type: 'objednávek', action: 'faktury' },
  { count: 12, type: 'plateb', action: 'spárováno' },
  { count: 8, type: 'zásilek', action: 'zaúčtováno' },
  { count: 23, type: 'rezervací', action: 'importováno' },
  { count: 6, type: 'dobropisů', action: 'zaúčtováno' },
  { count: 34, type: 'faktur', action: 'přeneseno' },
  { count: 18, type: 'plateb kartou', action: 'spárováno' },
  { count: 52, type: 'objednávek', action: 'faktury' },
  { count: 9, type: 'dobírek', action: 'zaúčtováno' },
  { count: 15, type: 'vratek', action: 'zpracováno' },
];

const NS = 'http://www.w3.org/2000/svg';

export default function SyncAnimation() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const targetRefs = useRef<(HTMLDivElement | null)[]>([]);
  const logRef = useRef<HTMLDivElement>(null);
  const counterRef = useRef<HTMLSpanElement>(null);
  const pathsRef = useRef<string[][]>([]);
  const syncCountRef = useRef(1247);
  const logIdxRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const layout = useCallback(() => {
    const canvas = canvasRef.current;
    const svg = svgRef.current;
    if (!canvas || !svg) return;

    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('width', String(W));
    svg.setAttribute('height', String(H));

    svg.querySelectorAll('.sync-path').forEach(p => p.remove());

    const sourceH = 34;
    const sourceGap = (H - SOURCES.length * sourceH) / (SOURCES.length + 1);
    const targetGap = (H - TARGETS.length * sourceH) / (TARGETS.length + 1);
    const targetX = W - 105;

    const targetCenters: number[] = [];
    TARGETS.forEach((_, i) => {
      const node = targetRefs.current[i];
      if (!node) return;
      const ny = targetGap * (i + 1) + sourceH * i;
      node.style.left = targetX + 'px';
      node.style.top = ny + 'px';
      targetCenters.push(ny + sourceH / 2);
    });

    const paths: string[][] = [];

    SOURCES.forEach((_, i) => {
      const node = nodeRefs.current[i];
      if (!node) return;

      const ny = sourceGap * (i + 1) + sourceH * i;
      node.style.left = '0px';
      node.style.top = ny + 'px';

      const sx = node.offsetWidth + 4;
      const sy = ny + sourceH / 2;
      const ex = targetX - 4;

      const sourcePaths: string[] = [];
      targetCenters.forEach(ey => {
        const cpOff = (ex - sx) * 0.42;
        const d = `M${sx},${sy} C${sx + cpOff},${sy} ${ex - cpOff},${ey} ${ex},${ey}`;
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', d);
        path.setAttribute('class', 'sync-path');
        svg.appendChild(path);
        sourcePaths.push(d);
      });
      paths.push(sourcePaths);
    });

    pathsRef.current = paths;
  }, []);

  const addLogEntry = useCallback((targetLabel: string) => {
    const logEl = logRef.current;
    if (!logEl) return;
    const msg = LOG_MESSAGES[logIdxRef.current % LOG_MESSAGES.length];
    logIdxRef.current++;
    const now = new Date();
    const time = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

    const item = document.createElement('div');
    item.className = 'sync-log-item';
    item.innerHTML =
      '<div class="sync-log-check">&#10003;</div>' +
      `<span class="sync-log-time">${time}</span>` +
      `<span class="sync-log-text"><strong>${msg.count} ${msg.type}</strong> ${msg.action} &rarr; ${targetLabel}</span>`;
    logEl.insertBefore(item, logEl.firstChild);
    while (logEl.children.length > 3 && logEl.lastChild) logEl.removeChild(logEl.lastChild);
  }, []);

  const sendPacket = useCallback((srcIdx: number, tgtIdx: number) => {
    const svg = svgRef.current;
    const d = pathsRef.current[srcIdx]?.[tgtIdx];
    const node = nodeRefs.current[srcIdx];
    const targetNode = targetRefs.current[tgtIdx];
    if (!svg || !d || !node || !targetNode) return;

    const color = SOURCES[srcIdx].color;
    const targetLabel = TARGETS[tgtIdx].label;

    node.classList.add('sending');
    setTimeout(() => node.classList.remove('sending'), 1500);

    const g = document.createElementNS(NS, 'g');
    g.setAttribute('opacity', '0');

    const glow = document.createElementNS(NS, 'circle');
    glow.setAttribute('r', '10');
    glow.setAttribute('fill', color);
    glow.setAttribute('opacity', '0.25');
    g.appendChild(glow);

    const dot = document.createElementNS(NS, 'circle');
    dot.setAttribute('r', '5');
    dot.setAttribute('fill', color);
    g.appendChild(dot);

    const anim = document.createElementNS(NS, 'animateMotion');
    anim.setAttribute('dur', '1.6s');
    anim.setAttribute('fill', 'freeze');
    anim.setAttribute('begin', 'indefinite');
    anim.setAttribute('calcMode', 'spline');
    anim.setAttribute('keySplines', '0.4 0 0.2 1');
    anim.setAttribute('keyTimes', '0;1');
    anim.setAttribute('path', d);
    g.appendChild(anim);

    const fade = document.createElementNS(NS, 'animate');
    fade.setAttribute('attributeName', 'opacity');
    fade.setAttribute('values', '0;1;1;0');
    fade.setAttribute('keyTimes', '0;0.1;0.85;1');
    fade.setAttribute('dur', '1.6s');
    fade.setAttribute('fill', 'freeze');
    fade.setAttribute('begin', 'indefinite');
    g.appendChild(fade);

    svg.appendChild(g);
    (anim as any).beginElement();
    (fade as any).beginElement();

    setTimeout(() => {
      targetNode.classList.add('receiving');
      setTimeout(() => targetNode.classList.remove('receiving'), 400);
      syncCountRef.current += Math.floor(Math.random() * 12) + 3;
      if (counterRef.current) {
        counterRef.current.textContent = syncCountRef.current.toLocaleString('cs-CZ') + ' synced';
      }
      addLogEntry(targetLabel);
    }, 1400);

    setTimeout(() => { g.remove(); }, 2200);
  }, [addLogEntry]);

  const loop = useCallback(() => {
    const srcIdx = Math.floor(Math.random() * SOURCES.length);
    const tgtIdx = Math.floor(Math.random() * TARGETS.length);
    sendPacket(srcIdx, tgtIdx);
    timerRef.current = setTimeout(loop, 1600 + Math.random() * 2000);
  }, [sendPacket]);

  useEffect(() => {
    const initTimer = setTimeout(() => {
      layout();
      loop();
    }, 500);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(layout, 250);
    };
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(resizeTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [layout, loop]);

  return (
    <div className="sync-animation">
      <div className="sync-top">
        <div className="sync-top-left">
          <div className="sync-top-dot" />
          <span className="sync-top-label">Live synchronizace</span>
        </div>
        <span className="sync-counter" ref={counterRef}>1 247 synced</span>
      </div>

      <div className="sync-canvas" ref={canvasRef}>
        <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" />
        {SOURCES.map((src, i) => (
          <div
            key={src.abbr}
            className="sync-node"
            ref={el => { nodeRefs.current[i] = el; }}
          >
            <div className="sync-node-icon" style={{ background: src.color + '14', color: src.color }}>{src.abbr}</div>
            {src.label}
          </div>
        ))}
        {TARGETS.map((tgt, i) => (
          <div
            key={tgt.abbr}
            className="sync-node target-node"
            ref={el => { targetRefs.current[i] = el; }}
          >
            <div className="sync-node-icon" style={{ fontSize: '10px', fontWeight: 800 }}>{tgt.abbr}</div>
            {tgt.label}
          </div>
        ))}
      </div>

      <div className="sync-log" ref={logRef}>
        <div className="sync-log-item">
          <div className="sync-log-check">&#10003;</div>
          <span className="sync-log-time">14:32</span>
          <span className="sync-log-text"><strong>45 objednávek</strong> faktury &rarr; Money S3</span>
        </div>
        <div className="sync-log-item">
          <div className="sync-log-check">&#10003;</div>
          <span className="sync-log-time">14:31</span>
          <span className="sync-log-text"><strong>12 plateb</strong> spárováno &rarr; Pohoda</span>
        </div>
        <div className="sync-log-item">
          <div className="sync-log-check">&#10003;</div>
          <span className="sync-log-time">14:28</span>
          <span className="sync-log-text"><strong>8 zásilek</strong> zaúčtováno &rarr; Abra</span>
        </div>
      </div>
    </div>
  );
}
